import { useEffect, useRef, useState } from "preact/hooks";
import { loadEnvelopes, onPeonSpeech } from "../audio";
import { registerLogoTap } from "../devsheet-hooks";
import { PIXEL_PEON, peonArtwork, peonMood, pixelPeonCell } from "../peon-art.ts";
import { useOpenCount } from "../openCount";
import { envelopeStateAt, ENVELOPE_FPS } from "../../shared/peonEnvelope.ts";
import { DEFAULT_SHIRT, type Shirt } from "../../shared/shirts.ts";
import { MOOD_FRAMES, SIT_STEP_MS, moodCell, nextMoodDelayMs, type PeonMoodName } from "../../shared/peon-mood.ts";
import { peonMoodSheet } from "../peon-mood-state.ts";

/**
 * Hard pixel-frame swaps. No transforms, crossfades or resampling during motion.
 *
 * `act` is the header's mood (docs/peons.md "Moods", shared/peon-mood.ts, flag `peonMoodsUI`): a
 * yawn or a hand rub played now and then from the mood sheet, or sat down until it changes, with
 * one half-way frame as he sits and as he gets up. Null (the flag off) draws exactly what it did.
 */
export function PixelPeon({ animated, shirt = DEFAULT_SHIRT, act = null }: { animated: boolean; shirt?: Shirt; act?: PeonMoodName | null }) {
  const mood = peonMood(useOpenCount());
  const art = peonArtwork(mood, shirt);
  const ref = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  const moodSheet = peonMoodSheet(mood);
  // The live animation reads these through refs, so a change of mood or of base sheet does not
  // restart the blink; `control` is how the effect below hears of a new act.
  const actRef = useRef(act);
  const artRef = useRef({ sheet: art.sheet, size: art.size * art.columns, height: art.size * art.rows, mood: moodSheet });
  artRef.current = { sheet: art.sheet, size: art.size * art.columns, height: art.size * art.rows, mood: moodSheet };
  const control = useRef<{ setAct: (next: PeonMoodName | null) => void; paint: () => void } | null>(null);

  useEffect(() => {
    if (!animated) return;
    setFailed(false);
    let active = true;
    const probe = new Image();
    probe.onerror = () => { if (active) setFailed(true); };
    probe.src = art.sheet;
    return () => { active = false; probe.onerror = null; };
  }, [animated, art.sheet]);

  useEffect(() => {
    const el = ref.current;
    if (!animated || failed || !el) return;
    const media = typeof matchMedia === "function" ? matchMedia("(prefers-reduced-motion: reduce)") : null;
    const timers = new Set<ReturnType<typeof setTimeout>>();
    let disposed = false, blink = 0, mouth = 0;
    let speechCleanup: (() => void) | undefined;
    // The mood: what he is doing (act), whether he is sat, and the mood frame showing now, if any.
    let act = actRef.current;
    let sat = act === "sit";
    let frame: [number, number] | null = null;
    const allowed = () => !media?.matches && !document.hidden;
    function draw(url: string, w: number, h: number, x: number, y: number) {
      el!.style.backgroundImage = `url(${url})`;
      el!.style.backgroundSize = `${w}px ${h}px`;
      el!.style.backgroundPosition = `${x}px ${y}px`;
    }
    function paint() {
      if (disposed) return;
      const now = artRef.current;
      const cell = PIXEL_PEON.size;
      // Reduced motion (or a hidden page): the mood's first frame, still.
      const still = !allowed();
      const moodAt = still && act ? (act === "sit" ? moodCell("sat", 0, 0) : moodCell(act, 0)) : frame ?? (sat ? moodCell("sat", blink, mouth) : null);
      if (moodAt) {
        draw(now.mood.url, now.mood.width, now.mood.height, -moodAt[0] * cell, -moodAt[1] * cell);
        return;
      }
      const { x, y } = pixelPeonCell(still ? 0 : blink, still ? 0 : mouth);
      draw(now.sheet, now.size, now.height, x, y);
    }
    function after(ms: number, fn: () => void) {
      const id = setTimeout(() => { timers.delete(id); if (!disposed) fn(); }, ms);
      timers.add(id);
    }
    function scheduleBlink() {
      if (!allowed()) return;
      after(2800 + Math.random() * 2500, () => {
        blink = 1; paint();
        after(70, () => {
          blink = 2; paint();
          after(90, () => {
            blink = 1; paint();
            after(70, () => { blink = 0; paint(); scheduleBlink(); });
          });
        });
      });
    }
    // ---------- moods: a yawn or a rub now and then; sat until something changes ----------
    function scheduleMood() {
      if (!allowed() || (act !== "yawn" && act !== "rub")) return;
      const kind = act;
      after(nextMoodDelayMs(kind, Math.random), () => playMood(kind, 0));
    }
    function playMood(kind: "yawn" | "rub", step: number) {
      const steps = MOOD_FRAMES[kind];
      // Done, the mood moved on, or he started talking (his mouth wins): back to the plain portrait.
      if (step >= steps.length || act !== kind || mouth !== 0 || !allowed()) {
        frame = null; paint();
        if (step >= steps.length || mouth !== 0) scheduleMood();
        return;
      }
      frame = moodCell(kind, steps[step].frame); paint();
      after(steps[step].ms, () => playMood(kind, step + 1));
    }
    function setAct(next: PeonMoodName | null) {
      if (next === act) return;
      const wasSat = act === "sit";
      act = next; frame = null;
      const nowSat = next === "sit";
      if (wasSat !== nowSat && allowed()) {
        // One half-way frame as he sits down or gets up.
        sat = false; frame = moodCell("getUp"); paint();
        after(SIT_STEP_MS, () => { frame = null; sat = nowSat; paint(); scheduleMood(); });
        return;
      }
      sat = nowSat; paint(); scheduleMood();
    }
    function resetMotion() {
      for (const timer of timers) clearTimeout(timer);
      timers.clear();
      blink = 0; mouth = 0; frame = null; sat = act === "sit"; paint(); scheduleBlink(); scheduleMood();
    }
    control.current = { setAct, paint };
    function stopSpeech() {
      speechCleanup?.(); speechCleanup = undefined;
      mouth = 0; paint();
    }
    const unsubscribe = onPeonSpeech((audio, dir, name) => {
      stopSpeech();
      let cancelled = false;
      let envelope: number[] | null = null;
      void loadEnvelopes(dir).then(map => {
        if (!cancelled && Array.isArray(map?.[name]) && map[name].length) envelope = map[name];
      }).catch(() => { /* The timed fallback keeps working without an envelope. */ });
      const tick = () => {
        if (audio.paused || audio.ended) { stopSpeech(); return; }
        mouth = envelope ? envelopeStateAt(envelope, audio.currentTime) : [1, 2, 1, 0][Math.floor(audio.currentTime / .12) % 4];
        paint();
      };
      const timer = setInterval(tick, 1000 / ENVELOPE_FPS);
      audio.addEventListener("pause", stopSpeech);
      audio.addEventListener("ended", stopSpeech);
      speechCleanup = () => {
        cancelled = true; clearInterval(timer);
        audio.removeEventListener("pause", stopSpeech);
        audio.removeEventListener("ended", stopSpeech);
      };
      tick();
    });
    document.addEventListener("visibilitychange", resetMotion);
    media?.addEventListener("change", resetMotion);
    resetMotion();
    return () => {
      disposed = true;
      control.current = null;
      for (const timer of timers) clearTimeout(timer);
      stopSpeech(); unsubscribe();
      document.removeEventListener("visibilitychange", resetMotion);
      media?.removeEventListener("change", resetMotion);
    };
  }, [animated, failed]);

  // A new mood reaches the running animation without restarting it; a new base sheet repaints.
  useEffect(() => {
    actRef.current = act;
    control.current?.setAct(act);
  }, [act]);
  useEffect(() => {
    control.current?.paint();
  }, [art.sheet]);
  // Fetch the mood sheet ahead of its first frame, so the first yawn is not a blank cell.
  useEffect(() => {
    if (!animated || !act) return;
    try {
      new Image().src = moodSheet.url;
    } catch {
      /* A preload is a nicety. */
    }
  }, [animated, !!act, moodSheet.url]);

  if (!animated || failed) return <img class="pixel-peon" data-mood={mood} src={art.still} alt="" aria-hidden="true" onClick={registerLogoTap} onError={e => {
    const fallback = "/ui/peon-original@3x.png";
    if (!e.currentTarget.src.endsWith(fallback)) e.currentTarget.src = fallback;
  }} />;
  return <div ref={ref} class="pixel-peon" data-mood={mood} aria-hidden="true" onClick={registerLogoTap} style={{ backgroundImage: `url(${art.sheet})`, backgroundSize: `${art.size * art.columns}px ${art.size * art.rows}px`, backgroundPosition: "0 0" }} />;
}
